import React, {useRef, useState, useEffect} from 'react';
import {Controller} from 'react-hook-form';
import moment from "moment/moment";
import {Popper} from '@mui/material';
import {TextToolTip} from "../table";
import {Svg} from "assets";
import {getNestedError} from "utils";

const activeStyles = {
    background: '#88c3e1',
    color: 'white',
};

const TimeInput = ({
                       allowError = true,
                       error,
                       labelHeader,
                       onChange,
                       name,
                       value,
                       minTime,
                       maxTime,
                       disabled,
                       defaultValue,
                       allowClearBtn = true,
                       classNameWrapper,
                   }) => {
    const [time, setTime] = useState({hour: '', minute: ''});
    const [anchorEl, setAnchorEl] = useState(null);
    const [inputFocused, setInputFocused] = useState(false);
    const [selected, setSelected] = useState(false);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;
    const hourRef = useRef(null);
    const minuteRef = useRef(null);
    const prevHasError = useRef(false);
    const inputRef = useRef(null);

    const hours = Array.from({length: 24}, (_, i) => String(i).padStart(2, '0'));
    const minutes = Array.from({length: 60}, (_, i) => String(i).padStart(2, '0'));

    useEffect(() => {
        if (!!error?.type && !prevHasError.current && inputRef.current) {
            inputRef.current.scrollIntoView({behavior: "smooth", block: "center"});
        }
        prevHasError.current = !!error?.type;
    }, [!!error?.type]);

    useEffect(() => {
        const source = value || (defaultValue ? moment(defaultValue).format('HH:mm') : null);
        if (source) {
            const [h, m] = source?.split(':');
            const newTime = {
                hour: String(h).padStart(2, '0'),
                minute: String(m).padStart(2, '0'),
            };
            setTime(prev => {
                if (prev.hour !== newTime.hour || prev.minute !== newTime.minute) {
                    return newTime;
                }
                return prev;
            });
        } else {
            setTime({hour: '', minute: ''});
        }
    }, [defaultValue, value, selected]);

    const convertTo24Hour = (t) => {
        return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
    };

    const isBeforeMinTime = (hour, minute) => {
        if (!minTime) return false;

        const [minH, minM] = minTime.split(':').map(Number);
        const h = hour?.length === 2 ? parseInt(hour, 10) : null;
        const m = minute?.length === 2 ? parseInt(minute, 10) : null;

        if (h === null) return false;
        if (m === null) return h < minH;
        if (h < minH) return true;
        return h === minH && m < minM;
    };

    const isAfterMaxTime = (hour, minute) => {
        if (!maxTime) return false;

        const [maxH, maxM] = maxTime.split(':').map(Number);
        const h = hour?.length === 2 ? parseInt(hour, 10) : null;
        const m = minute?.length === 2 ? parseInt(minute, 10) : null;

        if (h === null) return false;
        if (m === null) return h > maxH;
        if (h > maxH) return true;
        return h === maxH && m > maxM;
    };

    const handleChange = (field, value) => {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length > 2) return;

        const updatedTime = {...time, [field]: cleaned};

        if (isBeforeMinTime(updatedTime.hour, updatedTime.minute) ||
            isAfterMaxTime(updatedTime.hour, updatedTime.minute)) {
            return;
        }

        setTime(updatedTime);

        if (field === 'hour' && cleaned.length === 2) {
            minuteRef.current?.focus();
            setSelected(true);
        }

        if (field === 'minute' && cleaned.length === 0) {
            hourRef.current?.focus();
            setSelected(true);
        }
    };

    const sendTime = () => {
        if (time.hour.length === 2 && time.minute.length === 2) {
            if (isBeforeMinTime(time.hour, time.minute) ||
                isAfterMaxTime(time.hour, time.minute)) {
                return;
            }

            onChange?.({
                target: {
                    name,
                    value: convertTo24Hour(time),
                },
            });
        }
    };

    const handleArrowNavigation = (e, field) => {
        const input = field === 'hour' ? hourRef.current : minuteRef.current;
        const otherInput = field === 'hour' ? minuteRef.current : hourRef.current;

        if (!input) return;

        const cursorPos = input.selectionStart;

        if (e.key === 'ArrowRight' && field === 'hour') {
            if (cursorPos === input.value.length) {
                e.preventDefault();
                otherInput?.focus();
                if (otherInput) otherInput.setSelectionRange(0, 0);
                setSelected(true);
            }
        }

        if (e.key === 'ArrowLeft' && field === 'minute') {
            if (cursorPos === 0) {
                e.preventDefault();
                otherInput?.focus();
                if (otherInput) otherInput.setSelectionRange(otherInput.value.length, otherInput.value.length);
                setSelected(true);
            }
        }

        if (e.key === 'Backspace' && field === 'minute' && cursorPos === 0) {
            e.preventDefault();
            hourRef.current?.focus();
            hourRef.current?.setSelectionRange(hourRef.current.value.length, hourRef.current.value.length);
            setSelected(true);
        }
    };

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleClose = (e) => {
        e.stopPropagation();
        setAnchorEl(null);

        const source = defaultValue ? moment(defaultValue).format('HH:mm') : null;
        if (source) {
            const [h, m] = source?.split(':');
            const newTime = {
                hour: String(h).padStart(2, '0'),
                minute: String(m).padStart(2, '0'),
            };
            if (time?.hour !== newTime?.hour || time?.minute !== newTime?.minute) {
                sendTime();
            }
        }
    };

    const selectFromPopper = (field, val) => {
        setTime((prev) => {
            const updated = {...prev, [field]: val};

            const hourFilled = updated.hour.length === 2;
            const minuteFilled = updated.minute.length === 2;

            if (isBeforeMinTime(updated.hour, updated.minute) ||
                isAfterMaxTime(updated.hour, updated.minute)) {
                return prev;
            }

            if (hourFilled && minuteFilled) {
                onChange?.({
                    target: {
                        name,
                        value: convertTo24Hour(updated),
                    },
                });
            }

            return updated;
        });
    };

    function convertTo12HourFormat(time24) {
        const [hourStr, minute] = time24.split(':');
        let hour = parseInt(hourStr, 10);
        const period = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12;
        const formattedHour = String(hour).padStart(2, '0');
        return {
            hour: formattedHour,
            minute: minute,
            period: period,
            formatted: `${formattedHour}:${minute} ${period}`,
        };
    }

    return (
        <div
            className={`time-input-wrapper ${classNameWrapper ? classNameWrapper : ''}`}
            ref={inputRef}
            onClick={(e) => {
                if (!disabled && !selected) {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelected(true);

                    if (time?.hour?.length !== 2) {
                        setTimeout(() => {
                            hourRef.current?.focus();
                        }, 100);
                    } else {
                        setTimeout(() => {
                            minuteRef.current?.focus();
                        }, 100);
                    }
                }
            }}
        >
            {labelHeader && (
                <p className={`input-main-label-header ${error?.type ? "errorLabel" : ""}`}>
                    {labelHeader}
                </p>
            )}

            <div
                className={`time-input-box ${inputFocused ? 'time-input-focused' : ''} ${error?.type ? 'time-input-box-error' : ''} ${disabled ? 'time-input-box-disable' : ''}`}
                tabIndex={0}
            >
                {selected ? (
                    <div
                        className='flex-all-center'
                        onBlur={() => {
                            setSelected(false);
                            sendTime();
                        }}
                    >
                        <input
                            className={'time-input'}
                            style={{width: '22px'}}
                            ref={hourRef}
                            type="text"
                            placeholder="hh"
                            value={time.hour}
                            onChange={(e) => handleChange('hour', e.target.value)}
                            maxLength={2}
                            disabled={disabled}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                            onKeyDown={(e) => handleArrowNavigation(e, 'hour')}
                        />
                        <span>:</span>
                        <input
                            className={'time-input'}
                            style={{width: '30px', marginLeft: 2}}
                            ref={minuteRef}
                            type="text"
                            placeholder="mm"
                            value={time.minute}
                            onChange={(e) => handleChange('minute', e.target.value)}
                            maxLength={2}
                            onKeyDown={(e) => {
                                handleArrowNavigation(e, 'minute');
                            }}
                            disabled={disabled}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                        />
                    </div>
                ) : (
                    <div className='flex-all-center' style={{width: '100%', justifyContent: 'start'}}>
                        <p
                            style={{width: '22px', padding: '0 2px'}}
                            className={'time-input'}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelected(true);
                                setTimeout(() => {
                                    hourRef.current?.focus();
                                }, 100);
                            }}
                        >
                            {(time.hour && time.minute)
                                ? convertTo12HourFormat(`${time.hour}:${time.minute}`).hour
                                : 'hh'}
                        </p>
                        <span style={{fontSize: 14}}>:</span>
                        <TextToolTip
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelected(true);
                                setTimeout(() => {
                                    minuteRef.current?.focus();
                                }, 100);
                            }}
                            text={
                                (time.hour && time.minute)
                                    ? `${convertTo12HourFormat(`${time.hour}:${time.minute}`).minute} ${convertTo12HourFormat(`${time.hour}:${time.minute}`).period}`
                                    : 'mm'
                            }
                            content={
                                (time.hour && time.minute)
                                    ? `${convertTo12HourFormat(`${time.hour}:${time.minute}`).formatted}`
                                    : 'mm'
                            }
                            wrapperStyles={{width: 'fit-content', marginLeft: 4}}
                        />
                    </div>
                )}

                <div className='align-center' style={{gap: 2, marginLeft: 4}}>
                    {((time?.hour || time?.minute) && allowClearBtn) && (
                        <button
                            className='reset-btn clear-button-time-for-table'
                            type='button'
                            onClick={(e) => {
                                e.stopPropagation();
                                setTime({hour: '', minute: ''});
                                onChange?.({target: {name, value: ''}});
                            }}
                            style={{width: 18, height: 18}}
                        >
                            <Svg.CloseSvg style={{width: '100%', height: '100%'}}/>
                        </button>
                    )}
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClick(e);
                        }}
                        style={{cursor: 'pointer'}}
                        className='time-input-svg-block'
                    >
                        <Svg.AccessTimeSvg/>
                    </div>
                </div>
            </div>

            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                className="custom-select-time-box"
                placement="bottom-start"
                style={{position: 'absolute', zIndex: 2600}}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="hour-minute-box"
                    onMouseDown={e => e.preventDefault()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div>
                        {hours.map((item) => {
                            const [minH, minM] = minTime ? minTime.split(':').map(Number) : [null, null];
                            const [maxH, maxM] = maxTime ? maxTime.split(':').map(Number) : [null, null];
                            const hourNum = parseInt(item, 10);

                            let disableHour = false;
                            if (minTime && hourNum < minH) disableHour = true;
                            if (maxTime && hourNum > maxH) disableHour = true;

                            return (
                                <button
                                    key={`hour-${item}`}
                                    onClick={() => !disableHour && selectFromPopper('hour', item)}
                                    style={item === time.hour ? activeStyles : {}}
                                    type="button"
                                    disabled={disableHour}
                                >
                                    {item}
                                </button>
                            );
                        })}
                    </div>
                    <div>
                        {minutes.map((item) => {
                            const [minH, minM] = minTime ? minTime.split(':').map(Number) : [null, null];
                            const [maxH, maxM] = maxTime ? maxTime.split(':').map(Number) : [null, null];
                            const minuteNum = parseInt(item, 10);
                            const hourNum = parseInt(time.hour, 10);

                            let disableMinute = false;
                            if (minTime) {
                                if (hourNum < minH) disableMinute = true;
                                else if (hourNum === minH && minuteNum < minM) disableMinute = true;
                            }
                            if (maxTime) {
                                if (hourNum > maxH) disableMinute = true;
                                else if (hourNum === maxH && minuteNum > maxM) disableMinute = true;
                            }

                            return (
                                <button
                                    key={`minute-${item}`}
                                    onClick={() => !disableMinute && selectFromPopper('minute', item)}
                                    style={item === time.minute ? activeStyles : {}}
                                    type="button"
                                    disabled={disableMinute}
                                >
                                    {item}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </Popper>

            {allowError && <p className='error-message'>{error?.message}</p>}
            {open && <div className="beck-drop" onClick={handleClose}/>}
        </div>
    );
};

export const SelectTimeOnly = ({
                                   control,
                                   rules,
                                   name,
                                   labelHeader,
                                   minTime,
                                   maxTime,
                                   disabled,
                                   classNameWrapper,
                                   allowClearBtn = true,
                                   onChange,
                               }) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({field, formState: {errors}}) => {
                const error = getNestedError(errors, name);

                return (
                    <TimeInput
                        allowClearBtn={allowClearBtn}
                        {...field}
                        classNameWrapper={classNameWrapper}
                        labelHeader={labelHeader}
                        onChange={(e) => {
                            field.onChange(e.target.value);
                            if (onChange) onChange(e.target.value);
                        }}
                        allowError={false}
                        disabled={disabled}
                        minTime={minTime}
                        maxTime={maxTime}
                        error={error}
                    />
                );
            }}
        />
    );
};
